import { css } from '@emotion/react';
import { blue05 } from 'athena/styles/colors';
import { useState } from 'react';
import { SelectOption } from 'shared/components/inputs/nv-select';
import { black, gray3, gray7, primary } from 'styles/global_defaults/colors';
import {
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { ReactComponent as CheckedBox } from 'styles/icons/checked-box.svg';
import { ReactComponent as UncheckedBox } from 'styles/icons/unchecked-box.svg';

const styles = css`
  .multi-select-option {
    gap: ${halfSpacing}px;
    cursor: pointer;
    min-height: ${tripleSpacing}px;
    background-color: ${gray7};
    border-radius: ${quarterSpacing}px;
    border: 1px solid transparent;

    svg {
      min-width: ${largeSpacing}px;
    }

    .option-label {
      margin-bottom: 0;
      font-size: 16px;
      color: ${black};
    }
  }
  .checked {
    background-color: ${blue05};
    border: 1px solid ${primary};
    .option-label {
      color: ${primary};
      :before {
        color: ${primary} !important;
        border: 2px solid ${primary} !important;
      }
    }
  }
`;

type MultiSelectCheckListProps = {
  label: string;
  options: SelectOption[];
  onChange?: (event) => void;
  className?: string;
  selectedOptions?: string[];
  dataQa?: string;
  dataQaId?: string;
};

const MultiSelectCheckList = ({
  label,
  options,
  className,
  onChange,
  selectedOptions,
  dataQa,
  dataQaId,
}: MultiSelectCheckListProps) => {
  const [selected, setSelected] = useState<string[]>(selectedOptions || []);

  const handleChange = (value: string, checked: boolean) => {
    const updatedSelection = checked
      ? [...selected, value]
      : selected.filter((option) => option !== value);
    setSelected(updatedSelection);
    onChange(updatedSelection);
  };

  return (
    <div css={styles} className={className} data-qa={dataQa} data-qa-id={dataQaId}>
      <div className='input-label text-regular semi-bold mb-4'>{label}</div>
      {options.map(({ label: optionLabel, value: optionValue, dataQa: optionDataQa }) => {
        const isChecked = selected?.includes(optionValue);
        const sizeProps = { width: largeSpacing, height: largeSpacing };
        return (
          <div
            tabIndex={0}
            role='checkbox'
            aria-checked={isChecked}
            aria-label={typeof optionLabel === 'string' ? optionLabel : ''}
            key={optionValue}
            onClick={() => handleChange(optionValue, !isChecked)}
            className={`multi-select-option mb-2 d-flex align-items-center p-3 ${
              isChecked ? 'checked' : ''
            }`}
            data-qa={optionDataQa}
          >
            {isChecked ? (
              <CheckedBox {...sizeProps} color={primary} />
            ) : (
              <UncheckedBox {...sizeProps} color={gray3} />
            )}
            <div className='text-large-body option-label'>{optionLabel}</div>
          </div>
        );
      })}
    </div>
  );
};

export default MultiSelectCheckList;
